import { ModalConfirmation, ModalConfirmationList } from '@/components/common';
import { getPermissionErrorMessage } from '@/model/AccountModel';
import { DeleteOutlined, EllipsisOutlined } from '@ant-design/icons';
import { Button, Card, Dropdown, List, Spin } from 'antd';
import { ListGridType } from 'antd/es/list';
import { ItemType } from 'antd/es/menu/interface';
import { MenuProps } from 'antd/lib';
import cx from 'classnames';
import React, { useState } from 'react';
import styles from './gridListView.module.scss';

interface GridListProps {
	isUserAdmin?: boolean;
	gridConfig?: ListGridType;
	defaultIcon?: React.ReactNode;
	actions?: {
		onEdit?: (id: number) => void;
		onDelete?: (ids: number) => Promise<boolean>;
	};
	canEdit: boolean;
	canDelete: boolean;
	data?: GridDataProps<any>[];
	badgeCard?: boolean;
}

export interface GridDataProps<T> {
	cover?: React.ReactNode;
	title: string;
	subTitle?: string;
	id: number;
	extraData?: T;
}

const GridListView: React.FC<GridListProps> = ({ gridConfig, defaultIcon, data, actions, badgeCard, canEdit, canDelete, isUserAdmin }) => {
	//#region State Management Variables
	const [isLoading, setIsLoading] = useState<boolean>(false);
	//#endregion

	const { onDelete, onEdit } = actions;

	const createActionMenu = (id: number): Array<ItemType & { id: number }> => {
		if (isUserAdmin) {
			return [
				{ key: 'edit', label: 'Edit', id, title: getPermissionErrorMessage(canEdit), disabled: !canEdit },
				{ key: 'delete', label: 'Delete', id, title: getPermissionErrorMessage(canDelete), disabled: !canDelete },
			];
		} else {
			return [];
		}
	};

	const handleOnDelete = (key: number) => {
		const itemToDelete = data.find(x => x.id === key);
		if (itemToDelete) {
			if (badgeCard) {
				ModalConfirmation({
					width: '520px',
					content: (
						<div>
							<p>
								{_('AreYouSureYouWantToDelete')} <strong>{itemToDelete.title}</strong>
								{'?'}
							</p>
							<br />
							<span>
								{_('BadgeTemplateWillStillAvailable').replace('%1', '30')}
								{'  '}
								<span className={styles.archive}>
									<DeleteOutlined /> <span>{_('Archive')}</span>
								</span>
							</span>
						</div>
					),
					onConfirm: () => {
						onDelete(itemToDelete.id).finally(() => setIsLoading(false));
					},
				});
			} else {
				ModalConfirmationList({
					type: _('Delete'),
					translationKey: 'AreYouSureYouWantToDeleteVelocityVisionInstance',
					data: [{ Name: itemToDelete.title }],
					onConfirm: () => {
						onDelete(itemToDelete.id)
							.then(success => {
								if (success) {
									setIsLoading(true);
								}
							})
							.finally(() => setIsLoading(false));
					},
				});
			}
		}
	};

	const handleOnAction: MenuProps['onClick'] = event => {
		const elementId = Number(event.domEvent.currentTarget.id);
		if (event.key === 'edit') {
			onEdit(elementId);
		}
		if (event.key === 'delete') {
			handleOnDelete(elementId);
		}
	};

	const menuOnClickRouter = () => (e: any) => {
		e.domEvent.stopPropagation();
		handleOnAction(e);
	};

	const getBadgeCard = (item: GridDataProps<any>) => {
		return (
			<Card
				onClick={item.extraData?.onClick}
				className={cx(styles.cardContentViewBadge, styles.cardShadowBox)}
				id={item.id.toString()}
				cover={
					<div className={styles.coverContainerBadge}>
						<Dropdown
							className={styles.ellipsisBadge}
							menu={{
								items: createActionMenu(item.id),
								onClick: menuOnClickRouter(),
							}}>
							<Button>
								<EllipsisOutlined style={{ fontSize: 32 }} />
							</Button>
						</Dropdown>
						<span className={styles.coverCard}>{item?.cover || defaultIcon}</span>
						<div className={styles.cardTitle}>
							<p style={{ margin: '0px', fontWeight: 'bold', fontSize: '24px' }}>
								<strong>{item.title}</strong>
							</p>
							<p className={styles.pLastUpdated}>{item.subTitle}</p>
						</div>
					</div>
				}
			/>
		);
	};

	return (
		<Spin tip={`${_('Loading')}...`} size='large' spinning={isLoading}>
			<div className={styles.gridListContainer}>
				<List
					grid={
						gridConfig ?? {
							gutter: 2,
							xs: 1,
							sm: 2,
							md: 4,
							lg: 4,
							xl: 4,
							xxl: 5,
						}
					}
					className={styles.listItems}
					dataSource={data}
					renderItem={dataItem => (
						<List.Item
							className={cx(styles.listItem, {
								[styles.listItemBadge]: badgeCard === true,
							})}>
							{badgeCard === true ? (
								getBadgeCard(dataItem)
							) : (
								<Card
									onClick={dataItem.extraData?.onClick}
									className={cx(styles.cardContentView, styles.cardShadowBox)}
									id={dataItem.id.toString()}
									cover={
										<div className={styles.cardCoverView}>
											<Dropdown
												className={styles.ellipsis}
												menu={{
													items: createActionMenu(dataItem.id),
													onClick: menuOnClickRouter(),
												}}>
												<Button>
													<EllipsisOutlined />
												</Button>
											</Dropdown>
											<div className={styles.cardWrap}>
												<span className={styles.coverCard}>{dataItem?.cover || defaultIcon}</span>
												<div className={styles.cardTitle}>
													<p className={styles.title}>
														<strong>{dataItem.title}</strong>
													</p>
													<p className={styles.pLastUpdated}>{dataItem.subTitle}</p>
												</div>
											</div>
										</div>
									}
								/>
							)}
						</List.Item>
					)}
				/>
			</div>
		</Spin>
	);
};

export { GridListView };
